import { uploadImageAttachment, uploadDocument } from "../../services/api";
import { readDocxFile, readFileAsText, readPdfFile, readPptxFile, readXlsxFile } from "./converters";
import { generateFileId, getFileCategory, getCodeLanguageByExtension, getImageType } from "./utils";
import { ImageFileRecord, TextFileRecord } from "./types";

/**
 * Reads the file's content as text. We want REAL extractable text where
 * possible (not garbled binary) so local token counting matches what the
 * server sees.
 *
 * Order of preference:
 *   1. Format-aware parsers (plain text, .pdf via pdf.js, .docx, .pptx, .xlsx).
 *   2. Raw `readAsText` fallback for everything else (.doc, .rtf, .odt, etc.).
 */
async function readLocalDocContent(file: File): Promise<string | null> {
  const lowerName = file.name.toLowerCase();

  try {
    if (
      file.type === 'text/plain' ||
      file.type === 'text/markdown' ||
      file.type === 'text/csv' ||
      lowerName.endsWith('.md') ||
      lowerName.endsWith('.csv') ||
      lowerName.endsWith('.txt')
    ) {
      return await readFileAsText(file);
    }

    if (file.type === 'application/pdf' || lowerName.endsWith('.pdf')) {
      return await readPdfFile(file);
    }

    if (
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      lowerName.endsWith('.docx')
    ) {
      return await readDocxFile(file);
    }

    if (
      file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      lowerName.endsWith('.pptx')
    ) {
      return await readPptxFile(file);
    }

    if (
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      lowerName.endsWith('.xlsx')
    ) {
      return await readXlsxFile(file);
    }
  } catch {
    // Structured parse failed — fall through to the raw-text fallback so
    // we still get _something_ for token counting.
  }

  try {
    return await readFileAsText(file);
  } catch {
    return null;
  }
}

export async function processTextFile(
  file: File,
  updateTextFileRecord: (file: File, updates: Partial<TextFileRecord>) => void,
  handleFileError: (fileRecord: TextFileRecord, error: any, fallbackMsg?: string) => void
): Promise<TextFileRecord | null> {
  const category = getFileCategory(file);
  if (!category) {
    return null;
  }
  const type: TextFileRecord['type'] =
    category === 'pdf' || category === 'doc' || category === 'code' ? category : 'doc';

  const fileRecord: TextFileRecord = {
    name: file.name,
    size: file.size,
    id: generateFileId(file),
    content: null,
    isLoaded: false,
    type,
    url: '',
    codeLanguage: type === 'code' ? getCodeLanguageByExtension(file.name) : undefined,
  };

  (async () => {
    try {
      // The upload is the only hard dependency. The local content read is a
      // best-effort preview; if it fails we keep the record and rely on the
      // server-extracted content via `url`.
      const [contentResult, uploadResult] = await Promise.allSettled([
        readLocalDocContent(file),
        uploadDocument(file),
      ]);

      if (uploadResult.status === 'rejected') {
        throw uploadResult.reason;
      }

      const updates: Partial<TextFileRecord> = { url: uploadResult.value.file.url };

      if (contentResult.status === 'fulfilled' && contentResult.value !== null) {
        updates.content = contentResult.value;
      } else if (contentResult.status === 'rejected') {
        console.warn(
          `[processTextFile] Local content read failed for ${file.name}; continuing with server-side extraction.`,
          contentResult.reason
        );
      }

      updateTextFileRecord(file, updates);
    } catch (error) {
      handleFileError(
        fileRecord,
        error,
        `Error processing ${file.name.length > 50 ? file.name.slice(0, 50) + '...' : file.name}`
      );
    }
  })();

  return fileRecord;
}

export async function processImageFile(
  file: File,
  updateImageFileRecord: (file: File, updates: Partial<ImageFileRecord>) => void,
  handleFileError: (fileRecord: ImageFileRecord, error: any, fallbackMsg?: string) => void
): Promise<ImageFileRecord | null> {
  const type = getImageType(file);
  if (!type) {
    return null;
  }
  // Synchronous local preview so the card renders something immediately,
  // rather than sitting on a blank spinner until the upload resolves.
  const previewUrl = URL.createObjectURL(file);
  const fileRecord: ImageFileRecord = {
    name: file.name,
    size: file.size,
    id: generateFileId(file),
    isLoaded: false,
    type,
    url: '',
    previewUrl,
  };
  uploadImageAttachment(file)
    .then((image) => {
      updateImageFileRecord(file, {
        ...image,
        type,
        url: image.url,
        isLoaded: true,
        previewUrl: undefined,
      });
      URL.revokeObjectURL(previewUrl);
    })
    .catch((error) => {
      URL.revokeObjectURL(previewUrl);
      handleFileError(fileRecord, error, `Error uploading image ${file.name}`);
    });

  return fileRecord;
}
